<template>
  <div class="min-h-80 p-2 bg-gray-200 dark:bg-gray-900">
    <!-- Fiş Modal -->
    <modal name="receipt-modal" height="auto" width="600" :adaptive="true" :scrollable="true">
      <div class="p-4">
        <div class="flex items-center justify-between mb-3">
          <h3 class="text-sm font-semibold">Yakıt Fişi</h3>
          <button class="text-gray-500 hover:text-gray-700" @click="$modal.hide('receipt-modal')">
            <i class="fas fa-times"></i>
          </button>
        </div>
        <div v-if="receiptUrl">
          <div class="flex justify-end mb-2">
            <a :href="receiptUrl" download target="_blank" class="flex items-center px-3 py-1 text-xs text-white rounded" style="background-color: #f97316;">
              <i class="fas fa-download mr-1"></i>Fişi İndir
            </a>
          </div>
          <img :src="receiptUrl" class="w-full rounded" alt="Yakıt Fişi" />
        </div>
      </div>
    </modal>

    <!-- Detay Modal -->
    <modal name="purchase-detail-modal" height="auto" width="600" :adaptive="true" :scrollable="true">
      <div class="flex items-center justify-between p-4 border-b">
        <h3 class="text-sm font-semibold">Alım Detayı</h3>
        <button @click="$modal.hide('purchase-detail-modal')"><i class="fas fa-times"></i></button>
      </div>
      <div v-if="detailRow" class="p-4 space-y-3">
        <div class="grid grid-cols-2 gap-2 text-xs">
          <div><span class="text-gray-500">Plaka:</span> <span class="font-medium">{{ detailRow.vehiclePlate }}</span></div>
          <div><span class="text-gray-500">Sürücü:</span> <span class="font-medium">{{ detailRow.driverName }}</span></div>
          <div><span class="text-gray-500">İstasyon:</span> <span class="font-medium">{{ detailRow.stationName }}</span></div>
          <div><span class="text-gray-500">Tarih:</span> <span class="font-medium">{{ formatDate(detailRow.completedDate || detailRow.addDate) }}</span></div>
        </div>

        <!-- CAN-bus Yakıt Seviyesi -->
        <div v-if="detailRow.canbusFuelLevelBefore != null || detailRow.canbusFuelLevelAfter != null" class="p-2 bg-gray-50 border border-gray-200 rounded">
          <div class="text-xs font-medium text-gray-700 mb-2"><i class="fas fa-gas-pump mr-1"></i>CAN-bus Yakıt Seviyesi</div>
          <div class="grid grid-cols-3 gap-2 text-xs">
            <div class="text-center">
              <div class="text-xxs text-gray-500">Önceki</div>
              <div class="font-semibold">{{ detailRow.canbusFuelLevelBefore != null ? Number(detailRow.canbusFuelLevelBefore).toFixed(2) + '%' : '—' }}</div>
            </div>
            <div class="text-center">
              <div class="text-xxs text-gray-500">Sonraki</div>
              <div class="font-semibold">{{ detailRow.canbusFuelLevelAfter != null ? Number(detailRow.canbusFuelLevelAfter).toFixed(2) + '%' : '—' }}</div>
            </div>
            <div class="text-center">
              <div class="text-xxs text-gray-500">Değişim</div>
              <div class="font-semibold" :class="canbusChangeClass(detailRow)">{{ canbusChange(detailRow) }}</div>
            </div>
          </div>
        </div>

        <div>
          <div class="text-xs font-semibold mb-2">Yakıt Kalemleri</div>
          <div class="space-y-2">
            <div v-for="(it, idx) in (detailRow.items || [])" :key="idx" class="border rounded p-2 bg-gray-50">
              <div class="flex items-center justify-between">
                <div class="flex items-center space-x-2">
                  <span class="text-sm font-semibold">{{ it.fuelType }}</span>
                  <span v-if="it.isFillTank" class="px-1.5 py-0.5 text-white text-xxs rounded" style="background-color: #7c3aed;">TAM DOLUM</span>
                </div>
                <span class="text-xxs text-gray-500">{{ it.actualLiters != null ? it.actualLiters + ' Lt' : (it.approvedLiters + ' Lt') }}</span>
              </div>
              <div class="grid grid-cols-2 gap-2 text-xxs mt-1">
                <div><span class="text-gray-500">Birim:</span> <span class="font-medium">{{ it.unitPrice != null ? Money(it.unitPrice) + ' TL' : '—' }}</span></div>
                <div><span class="text-gray-500">Tutar:</span> <span class="font-medium">{{ it.totalAmount != null ? Money(it.totalAmount) + ' TL' : '—' }}</span></div>
              </div>
            </div>
            <div v-if="(detailRow.items || []).length === 0" class="text-xxs text-gray-400 text-center py-2">Kalem yok</div>
          </div>
        </div>

        <div v-if="detailRow.receiptFileUrl">
          <button class="px-3 py-1 text-white text-xs rounded" style="background-color: #f97316;" @click="viewReceipt(detailRow.receiptFileUrl)">
            <i class="fas fa-receipt mr-1"></i>Fişi Gör
          </button>
        </div>
      </div>
    </modal>

    <div class="h-full p-2 bg-gray-50 dark:bg-gray-800 rounded shadow-sm">
      <!-- Tab Seçimi -->
      <div class="flex border-b mb-3">
        <button class="px-4 py-2 text-sm font-medium" :class="activeTab === 'purchases' ? 'border-b-2 border-red-500 text-red-600' : 'text-gray-500'" @click="activeTab = 'purchases'; getPurchases()">
          Yakıt Alım Raporu
        </button>
        <button class="px-4 py-2 text-sm font-medium" :class="activeTab === 'consumption' ? 'border-b-2 border-red-500 text-red-600' : 'text-gray-500'" @click="activeTab = 'consumption'; getConsumption()">
          Tüketim Karşılaştırma
        </button>
      </div>

      <!-- Filtreler -->
      <div class="flex items-center space-x-3 mb-3 p-2">
        <div class="w-40">
          <diffInput v-model="startDate" title="Başlangıç" type="date" />
        </div>
        <div class="w-40">
          <diffInput v-model="endDate" title="Bitiş" type="date" />
        </div>
        <div class="w-40">
          <diffInput v-model="vehiclePlate" title="Plaka" type="text" />
        </div>
        <asyncBtn icon="fas fa-search" text="Sorgula" class="w-28 btn mt-5" @click="activeTab === 'purchases' ? getPurchases() : getConsumption()" />
      </div>

      <!-- Alım Raporu -->
      <div v-if="activeTab === 'purchases'">
        <!-- Özet -->
        <div v-if="summary.totalPurchases" class="grid grid-cols-4 gap-3 mb-3 p-2">
          <div class="bg-blue-50 dark:bg-gray-700 p-3 rounded text-center">
            <div class="text-xxs text-gray-500">Toplam Alım</div>
            <div class="text-lg font-bold">{{ summary.totalPurchases }}</div>
          </div>
          <div class="bg-green-50 dark:bg-gray-700 p-3 rounded text-center">
            <div class="text-xxs text-gray-500">Toplam Litre</div>
            <div class="text-lg font-bold">{{ summary.totalLiters || 0 }}</div>
          </div>
          <div class="bg-yellow-50 dark:bg-gray-700 p-3 rounded text-center">
            <div class="text-xxs text-gray-500">Toplam Tutar</div>
            <div class="text-lg font-bold">{{ Money(summary.totalCost || 0) }} TL</div>
          </div>
          <div class="bg-purple-50 dark:bg-gray-700 p-3 rounded text-center">
            <div class="text-xxs text-gray-500">Ort. Birim Fiyat</div>
            <div class="text-lg font-bold">{{ summary.avgUnitPrice || 0 }} TL</div>
          </div>
        </div>

        <LyrausTable
          :data="purchaseList"
          :columns="purchaseColumns"
          :isLoading="load"
          :alternateRowBg="['white', 'gray-100']"
          :rightExtraSlot="true"
          :buttonsColumn="{ label: 'İşlem' }"
          :containerClass="'p-2 bg-gray-50 dark:bg-gray-800 rounded shadow-sm'"
          :innerThClass="'py-2 text-left text-xxs font-medium bg-red-300 tracking-wider border-gray-200'"
          :innerTdClass="'whitespace-nowrap text-xxs bg-red-200 py-2'"
        >
          <template v-slot:rowButtons="{ item, columnKey }">
            <span v-if="columnKey === 'itemsSummary'" class="text-xxs">
              <span v-for="(it, idx) in (item.items || [])" :key="idx" class="inline-block mr-2">
                <span class="font-medium">{{ it.fuelType }}</span>
                <span v-if="it.isFillTank" class="ml-1 px-1 py-0.5 text-white text-xxs rounded" style="background-color: #7c3aed;">TAM DOLUM</span>
                <span v-else>: {{ it.actualLiters != null ? it.actualLiters : it.approvedLiters }} Lt</span>
              </span>
            </span>
          </template>
          <template v-slot:colButtons="{ item }">
            <div class="flex items-center justify-end space-x-1 px-2">
              <button v-if="item.receiptFileUrl" class="px-2 py-1 text-white text-xxs rounded" style="background-color: #f97316;" @click="viewReceipt(item.receiptFileUrl)" title="Fiş">
                <i class="fas fa-receipt"></i>
              </button>
              <button class="px-2 py-1 text-white text-xxs rounded" style="background-color: #6b7280;" @click="openDetail(item)">
                <i class="fas fa-info-circle mr-1"></i>Detay
              </button>
            </div>
          </template>
        </LyrausTable>
      </div>

      <!-- Tüketim Raporu -->
      <div v-if="activeTab === 'consumption'">
        <LyrausTable
          :data="consumptionList"
          :columns="consumptionColumns"
          :isLoading="load"
          :alternateRowBg="['white', 'gray-100']"
          :containerClass="'p-2 bg-gray-50 dark:bg-gray-800 rounded shadow-sm'"
          :innerThClass="'py-2 text-left text-xxs font-medium bg-red-300 tracking-wider border-gray-200'"
          :innerTdClass="'whitespace-nowrap text-xxs bg-red-200 py-2'"
        />
      </div>
    </div>
  </div>
</template>

<script>
import asyncBtn from "@/components/general/asyncBtn.vue";
import diffInput from "@/components/general/diffInput.vue";
import axios from "axios";
import moment from "moment";
import "moment/locale/tr";
import { fuel } from "@/networking/urlmanager";

export default {
  name: "fuel-report-module",
  components: { asyncBtn, diffInput },
  data() {
    return {
      load: false,
      activeTab: "purchases",
      startDate: "",
      endDate: "",
      vehiclePlate: "",
      summary: {},
      purchaseList: [],
      consumptionList: [],
      receiptUrl: "",
      detailRow: null,
      purchaseColumns: [
        { key: "completedDate", label: "Tarih", isDate: true },
        { key: "vehiclePlate", label: "Plaka" },
        { key: "driverName", label: "Sürücü" },
        { key: "stationName", label: "İstasyon" },
        { key: "itemsSummary", label: "Kalemler", dontDisplay: true },
        { key: "totalLiters", label: "Toplam Litre" },
        { key: "totalPrice", label: "Toplam Tutar (TL)" },
      ],
      consumptionColumns: [
        { key: "vehiclePlate", label: "Plaka" },
        { key: "purchaseCount", label: "Alım Sayısı" },
        { key: "totalLiters", label: "Toplam Litre" },
        { key: "totalCost", label: "Toplam Tutar (TL)" },
        { key: "avgFuelLevelBefore", label: "Ort. Seviye Önce (%)" },
        { key: "avgFuelLevelAfter", label: "Ort. Seviye Sonra (%)" },
        { key: "avgFuelLevelChange", label: "Ort. Değişim (%)" },
      ],
    };
  },
  methods: {
    formatDate(d) { return d ? moment(d).format("LLL") : "—"; },
    canbusChange(row) {
      if (!row) return "—";
      const b = row.canbusFuelLevelBefore;
      const a = row.canbusFuelLevelAfter;
      if (b == null || a == null) return "—";
      const diff = Number(a) - Number(b);
      const sign = diff > 0 ? "+" : "";
      return sign + diff.toFixed(2) + "%";
    },
    canbusChangeClass(row) {
      if (!row) return "";
      const b = row.canbusFuelLevelBefore;
      const a = row.canbusFuelLevelAfter;
      if (b == null || a == null) return "text-gray-500";
      const diff = Number(a) - Number(b);
      return diff > 0 ? "text-green-600" : (diff < 0 ? "text-red-600" : "text-gray-500");
    },
    getPurchases() {
      this.load = true;
      this.purchaseList = [];
      let url = fuel.reportPurchases + "?";
      if (this.startDate) url += "startDate=" + this.startDate + "&";
      if (this.endDate) url += "endDate=" + this.endDate + "&";
      if (this.vehiclePlate) url += "vehiclePlate=" + this.vehiclePlate;

      axios.get(url, { headers: { Authorization: "Bareer " + this.$store.state.userData.token } })
        .then((res) => {
          this.purchaseList = res.data.data.detail || [];
          this.summary = res.data.data.summary || {};
        })
        .catch((err) => { this.errorBox(err.response); })
        .finally(() => { this.load = false; });
    },
    getConsumption() {
      this.load = true;
      this.consumptionList = [];
      let url = fuel.reportConsumption + "?";
      if (this.startDate) url += "startDate=" + this.startDate + "&";
      if (this.endDate) url += "endDate=" + this.endDate + "&";
      if (this.vehiclePlate) url += "vehiclePlate=" + this.vehiclePlate;

      axios.get(url, { headers: { Authorization: "Bareer " + this.$store.state.userData.token } })
        .then((res) => { this.consumptionList = res.data.data || []; })
        .catch((err) => { this.errorBox(err.response); })
        .finally(() => { this.load = false; });
    },
    viewReceipt(url) {
      this.receiptUrl = url;
      this.$modal.show("receipt-modal");
    },
    openDetail(item) {
      this.detailRow = item;
      this.$modal.show("purchase-detail-modal");
    },
  },
  created() { this.getPurchases(); },
};
</script>
